import {
  onMounted, onUnmounted, ref, watch,
} from 'vue';
import { api } from '@/api';
import { SEND_STATE } from '@/helpers/planSendStatus';

export const usePlanSendStatus = (getPlanId, getRevision) => {
  const status = ref(null);
  const loading = ref(true);
  const refreshing = ref(false);
  const loadError = ref(false);
  let timer;
  let generation = 0;
  let active = true;
  let refreshStatus;

  const cancelTimer = () => {
    clearTimeout(timer);
    timer = null;
  };

  const schedule = () => {
    cancelTimer();
    const pending = [SEND_STATE.NOT_SENT, SEND_STATE.PARTIALLY_SENT].includes(status.value?.state);
    if (active && (pending || status.value?.state === SEND_STATE.SENT || loadError.value)) {
      timer = setTimeout(() => {
        if (document.hidden) schedule();
        else refreshStatus();
      }, pending || loadError.value ? 30000 : 60000);
    }
  };

  const setStatus = (value) => {
    generation += 1;
    status.value = value;
    loading.value = false;
    refreshing.value = false;
    loadError.value = false;
    schedule();
  };

  refreshStatus = async () => {
    if (!active || refreshing.value) return;
    cancelTimer();
    const requestGeneration = generation;
    refreshing.value = true;
    try {
      const response = await api.getCrmPlanSendStatus(getPlanId());
      if (active && generation === requestGeneration) {
        status.value = response.data;
        loadError.value = false;
      }
    } catch (error) {
      if (active && generation === requestGeneration) loadError.value = true;
    } finally {
      if (active && generation === requestGeneration) {
        loading.value = false;
        refreshing.value = false;
        schedule();
      }
    }
  };

  const restart = () => {
    generation += 1;
    cancelTimer();
    status.value = null;
    loading.value = true;
    refreshing.value = false;
    refreshStatus();
  };

  const onActivate = () => {
    if (!document.hidden) refreshStatus();
  };

  watch([getPlanId, getRevision], restart);
  onMounted(() => {
    refreshStatus();
    window.addEventListener('focus', onActivate);
    document.addEventListener('visibilitychange', onActivate);
  });
  onUnmounted(() => {
    active = false;
    generation += 1;
    cancelTimer();
    window.removeEventListener('focus', onActivate);
    document.removeEventListener('visibilitychange', onActivate);
  });

  return {
    status, loading, refreshing, loadError, refreshStatus, setStatus,
  };
};
